import { color } from "@funded-here-interface/common";
import React, { useEffect } from "react";
import uploadIcon from "../../assets/cloud_upload_24px.png";
import { Anchor, Group, Text } from "@mantine/core";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  DocumentType,
  useDocumentUpload,
} from "../../hooks/handleBorFIleUpload";
import { RootState } from "../../store";
import { useStyle } from "./FileUpload.styles";
import useUploadOnboarding from "../../hooks/useUploadOnboarding";
import {
  File as FileEnum,
  FileType,
} from "@funded-here-interface/common/src/constant/enum";
import { toast } from "react-toastify";
import {
  UploadedFile,
  setBalance,
  setBankDoc,
  setBusinessDoc,
  setCertificateOfIncorporationDoc,
  setFinancialStatementDoc,
  setFundManagerLicenseDoc,
  setInvestmentMandateDoc,
  setProofDoc,
  setSalaryProofDoc,
  setShareDoc,
  setSupportingDocumentsWithSuppliersDoc,
  setProofOfBankAccountDetailsDoc,
  setCrsSelfCertificationFormDoc,
  setCompanyConstitutionDoc,
} from "../../features/merchant/doc";
import useDeleteFile from "../../hooks/useDeleteFile";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import useGetFhDocument from "../../hooks/useFhDocument";

type FileUploadComponentProps = {
  fileId: string;
  number: number;
  FileName: string;
  existingFiles?: UploadedFile[];
  onFileChange?: (files: UploadedFile[]) => void;
  errors?: { [key: string]: string };
  onClearError?: (errorKey: string) => void;
  isIndividualInvestor?: boolean;
  isSupplier?: boolean;
  isIspv?: boolean;
};

/**
 * Configuration mapping fileId to Redux actions and document type keys
 * Eliminates the need for 3 massive switch statements
 */
type DownloadableFile = "CRS Self-Certification Form (Entity).pdf";

const FILE_CONFIG: Record<
  string,
  {
    documentType: DocumentType;
    setAction: (files: UploadedFile[]) => any;
    errorKey: string;
    description?: (
      props: {
        isIndividualInvestor?: boolean;
        isSupplier?: boolean;
        isIspv?: boolean;
      },
      handleDownload?: (fileName: DownloadableFile) => void,
      classes?: any
    ) => React.ReactNode;
  }
> = {
  [FileEnum.BUSINESS_PROFILE]: {
    documentType: "business",
    setAction: (files) => setBusinessDoc(files),
    errorKey: "businessProfileFile",
    description: () => (
      <>
        Latest company profile from your company registry showing registration
        number, nature of business, business address, directors, and
        shareholders.
      </>
    ),
  },
  [FileEnum.SHAREHOLDERS_DIRECTORS_REGISTERS]: {
    documentType: "share",
    setAction: (files) => setShareDoc(files),
    errorKey: "shareholderRegister",
  },
  [FileEnum.BALANCE_SHEET]: {
    documentType: "balance",
    setAction: (files) => setBalance(files),
    errorKey: "balanceSheet",
    description: () => (
      <>
        Recent financial statements, management accounts, 6 to 12 months' bank
        statements, and/or proof of historical SKU sales.
      </>
    ),
  },
  [FileEnum.PROOF_OF_ADDRESS]: {
    documentType: "proof",
    setAction: (files) => setProofDoc(files),
    errorKey: "proofOfAddress",
    description: ({ isIndividualInvestor }) =>
      isIndividualInvestor ? (
        <>
          Recent (within last 3 months) utility bill, bank statement, or
          government letter showing residential address.
        </>
      ) : (
        <>
          Recent (within last 3 months) utility bill, bank statement, or
          government letter showing residential address for ALL:
          <br />• Directors;
          <br />• Shareholders with 25% and above shareholding percentage; and
          <br />• Ultimate beneficial owners (UBOs).
        </>
      ),
  },
  [FileEnum.BANK_STATEMENT]: {
    documentType: "bank",
    setAction: (files) => setBankDoc(files),
    errorKey: "bankStatement",
    description: ({ isIndividualInvestor }) =>
      isIndividualInvestor ? (
        <>
          Bank statement header showing account name, number, and bank name. The
          account name must match your legal name. This will be the bank account
          used to receive or remit funds.
        </>
      ) : (
        <>
          Bank statement header showing account name, number, and bank name. The
          account name must match the legal name of the company. This will be
          the bank account used to receive or remit funds.
        </>
      ),
  },
  [FileEnum.SALARY_PROOF]: {
    documentType: "salaryProof",
    setAction: (files) => setSalaryProofDoc(files),
    errorKey: "salaryProof",
    description: () => (
      <>
        Employment letter, latest income tax filing, recent bank statements,
        investment account statements, or annual income statements. These are
        used to confirm you meet the *Accredited Investor criteria.
        <br />
        <br />
        <Text style={{ fontStyle: "italic" }}>
          * Accredited Investor under Section 4A of the Securities and Futures
          Act 2001 means an individual whose net personal assets exceed
          SGD2million (or its equivalent in a foreign currency); or whose
          financial assets exceed SGD1million (or its equivalent in a foreign
          currency); or income of the preceding 12 months is not less than
          SGD300,000.00 (or its equivalent in a foreign currency).
        </Text>
      </>
    ),
  },
  [FileEnum.FINANCIAL_STATEMENT]: {
    documentType: "financialStatement",
    setAction: (files) => setFinancialStatementDoc(files),
    errorKey: "financialStatement",
    description: ({ isIspv }) =>
      isIspv ? (
        <>
          Recent financial statements, management accounts, and/or 6 to 12
          months' bank statements.
        </>
      ) : (
        <>
          Recent financial statements to confirm the company meets the
          *Accredited Investor criteria.
          <br />
          <br />
          <Text style={{ fontStyle: "italic" }}>
            *Accredited Investor under Section 4A of the Securities and Futures
            Act 2001 means a corporation with net assets exceeding SGD10million
            (or its equivalent in foreign currency), as determined by the most
            recent audited balance sheet of the corporation (or where not
            required to prepare audited accounts regularly, a balance sheet
            certified by the corporation as giving a true and fair view of its
            state of affairs as of the date of the balance sheet) which must be
            within the preceding 12 months.
          </Text>
        </>
      ),
  },
  [FileEnum.INVESTMENT_MANDATE]: {
    documentType: "investmentMandate",
    setAction: (files) => setInvestmentMandateDoc(files),
    errorKey: "investmentMandate",
  },
  [FileEnum.FUND_MANAGER_LICENSE]: {
    documentType: "fundManagerLicense",
    setAction: (files) => setFundManagerLicenseDoc(files),
    errorKey: "fundManagerLicense",
  },
  [FileEnum.CERTIFICATE_OF_INCORPORATION]: {
    documentType: "certificateOfIncorporation",
    setAction: (files) => setCertificateOfIncorporationDoc(files),
    errorKey: "certificateOfIncorporation",
    description: () => (
      <>
        Official certificate from your company registry showing the date and
        place of incorporation.
      </>
    ),
  },
  [FileEnum.SUPPORTING_DOCUMENTS_WITH_SUPPLIERS]: {
    documentType: "supportingDocumentsWithSuppliers",
    setAction: (files) => setSupportingDocumentsWithSuppliersDoc(files),
    errorKey: "supportingDocumentsWithSuppliers",
    description: ({ isSupplier }) =>
      isSupplier ? (
        <>
          As part of our independent verification, to provide either of the
          following:
          <br />• Past invoices or purchase orders issued to prove an existing
          business relationship with the stipulated merchant; or <br />• Product
          catalogues or brochures to evidence the type of products offered as a
          supplier.
        </>
      ) : (
        <>
          Recent purchase orders, invoices, or contracts with suppliers related
          to commercial collaboration proposal.
        </>
      ),
  },
  [FileEnum.PROOF_OF_BANK_ACCOUNT_DETAILS]: {
    documentType: "proofOfBankAccountDetails",
    setAction: (files) => setProofOfBankAccountDetailsDoc(files),
    errorKey: "proofOfBankAccountDetails",
    description: ({ isIspv }) =>
      isIspv ? (
        <>
          Bank statement header showing account name, number, and bank name. The
          account name must match the legal name of the company.
        </>
      ) : (
        <>
          Bank statement header showing account name, number, and bank name. The
          account name must match the legal name of the company. This will be
          the bank account used to receive or remit funds.
        </>
      ),
  },
  [FileEnum.CRS_SELF_CERTIFICATION_FORM]: {
    documentType: "crsSelfCertificationForm",
    setAction: (files) => setCrsSelfCertificationFormDoc(files),
    errorKey: "crsSelfCertificationForm",
    description: (
      { isIndividualInvestor, isSupplier },
      handleDownload,
      classes
    ) => (
      <>
        Completed Common Reporting Standard (CRS) form to declare the company's
        tax residency for international tax reporting purposes.
        <br />
        <br />
        Please click{" "}
        <Anchor
          color={color.FHGREEN}
          underline={false}
          onClick={() =>
            handleDownload?.("CRS Self-Certification Form (Entity).pdf")
          }
          className={classes.agreement}
        >
          here
        </Anchor>{" "}
        to download the CRS Self-Certification Form.
      </>
    ),
  },
  [FileEnum.COMPANY_CONSTITUTION]: {
    documentType: "companyConstitution",
    setAction: (files) => setCompanyConstitutionDoc(files),
    errorKey: "companyConstitution",
    description: () => (
      <>
        Company's governing document that set out the structure, purpose, and
        internal rules, as issued or filed with the relevant corporate registry.
      </>
    ),
  },
};

function FileUploadComponent({
  fileId,
  number,
  FileName,
  existingFiles = [],
  onFileChange,
  errors,
  onClearError,
  isIndividualInvestor = false,
  isSupplier = false,
  isIspv = false,
}: FileUploadComponentProps) {
  const { classes } = useStyle();
  const useUploadOnboardingMutation = useUploadOnboarding();
  const token = useSelector((state: RootState) => state.auth.token);
  const useDeleteFileMutation = useDeleteFile();
  const dispatch = useDispatch();
  const useGetFhDocumentMutation = useGetFhDocument();

  // Get configuration for this file type
  const config = FILE_CONFIG[fileId];

  // Use the generic document upload hook
  const { documents, appendDocument } = useDocumentUpload(config?.documentType);
  // Get the correct error for this file type
  const hasError = config && errors?.[config.errorKey];

  // Initialize with existing files or Redux state
  const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>(
    existingFiles?.length > 0 ? existingFiles : documents ?? []
  );

  const fileNameToDownloadName: Record<DownloadableFile, string> = {
    "CRS Self-Certification Form (Entity).pdf": "CRS Self-Certification Form",
  };

  const handleDownload = (fileName: DownloadableFile) => {
    useGetFhDocumentMutation.mutate(
      { fileName },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, fileNameToDownloadName[fileName], FileType.PDF);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  useEffect(() => {
    setSelectedFiles(existingFiles || []);
  }, [existingFiles]);

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e?.target?.files || e.target.files.length === 0 || !config) return;

    const file = e.target.files[0];

    const fileNameExists = selectedFiles.some(
      (selectedFile) => selectedFile.name === file.name
    );

    if (fileNameExists) {
      toast.error("A file with the same name already exists.");
      return;
    }

    useUploadOnboardingMutation.mutate(
      { file, token, name: `${FileName}-${file.name}` },
      {
        onSuccess: (filePath: string) => {
          const newFile: UploadedFile = {
            name: file.name,
            path: filePath,
          };

          // Append to Redux via generic hook
          appendDocument(newFile, documents);

          // Update local state
          const updatedFiles = [...selectedFiles, newFile];
          setSelectedFiles(updatedFiles);

          // Dispatch Redux action via config
          dispatch(config.setAction(updatedFiles));

          // Clear error for this field when file is uploaded
          if (onClearError && config.errorKey) {
            onClearError(config.errorKey);
          }

          // Notify parent
          onFileChange?.(updatedFiles);

          e.target.value = "";
        },
        onError: (error) => {
          e.target.value = "";
          toast.error((error as Error).message);
        },
      }
    );
  };

  const handleDeleteFile = (filePath: string) => {
    if (!config) return;

    useDeleteFileMutation.mutate(
      { token, filePath },
      {
        onSuccess: () => {
          const updatedFiles = selectedFiles.filter(
            (file) => file.path !== filePath
          );

          // Update Redux via config
          dispatch(config.setAction(updatedFiles));

          // Update local state
          setSelectedFiles(updatedFiles);

          // Notify parent
          onFileChange?.(updatedFiles);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <>
      <div className={classes.docContainer}>
        <Group style={{ padding: 20 }}>
          <Text className={classes.docName}>{`${number}. ${FileName}`}</Text>
          <div className={classes.uploadButtonContainer}>
            <label htmlFor={fileId} className={classes.label}>
              <input
                type="file"
                name={fileId}
                id={fileId}
                accept=".pdf"
                style={{ display: "none" }}
                onChange={handleUploadFile}
              />
              <img src={uploadIcon} alt="upload" />{" "}
              <span style={{ paddingTop: "-15px" }}> Upload </span>
            </label>
          </div>
        </Group>
        {config?.description && (
          <Text className={classes.additionalInfo}>
            {config.description(
              { isIndividualInvestor, isSupplier, isIspv },
              handleDownload,
              classes
            )}
          </Text>
        )}
        {hasError && <span className={classes.errorText}>{hasError}</span>}
      </div>
      {selectedFiles.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Text className={classes.uploadedFileHeader}>Uploaded Files:</Text>
          {selectedFiles?.map((file) => (
            <Group key={file.path}>
              <div className={classes.uploadedFileName}>
                <Text className={classes.uploadedDocName}>{file.name}</Text>
                <Text
                  onClick={() => handleDeleteFile(file.path)}
                  className={classes.removeText}
                >
                  Remove
                </Text>
              </div>
            </Group>
          ))}
        </div>
      )}
    </>
  );
}

export default FileUploadComponent;
